import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  withStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DeleteIcon from "@material-ui/icons/Delete";
import products from "../../service/products";
import { getTenant } from "../../libs/tenant";
import { formatCurrency, formatQuantity, parseDecimal } from "../../libs/format";
import { format, parseISO } from "date-fns";
import { updateItemQuantity } from "../../libs/linkPrefill";

// Valor de um campo adicional formatado para exibição (datas em dd/MM/yyyy).
const formatExtra = (value, extraField) => {
  if (value == null || value === "") return "";
  if (extraField && extraField.type_input === "date") {
    try {
      return format(parseISO(value), "dd/MM/yyyy");
    } catch (e) {
      return value;
    }
  }
  return value;
};

// Resolve a tabela de preço padrão do grupo: preferir "Valor", senão a primeira.
const resolveDefaultTable = (priceTables) => {
  const tables = priceTables || [];
  return (
    tables.find((t) => String(t.name || "").trim().toLowerCase() === "valor") ||
    tables[0] ||
    null
  );
};

// Preço de um produto na tabela padrão (a partir de product.prices).
const priceForTable = (product, priceTableId) => {
  if (!product || !priceTableId) return null;
  const pr = (product.prices || []).find(
    (p) => p.price_table_id === priceTableId
  );
  return pr ? parseDecimal(pr.price) : null;
};

// Recalcula os derivados de cada item distribuindo o "valor adicional total"
// entre os produtos: (total / soma do total em unidade de todos os produtos)
// x qtd por embalagem => adicional rateado por unidade. O adicional individual
// do produto (também por unidade) é somado a esse valor.
const recompute = (items, additionalTotal) => {
  const total = Number(additionalTotal) || 0;
  const sumPhysical = items.reduce(
    (a, it) => a + (Number(it.total_quantity) || 0),
    0
  );
  const rateByPhysicalUnit = sumPhysical ? total / sumPhysical : 0;
  return items.map((it) => {
    const qty = Number(it.quantity) || 0;
    const packageQty = Number(it.package_quantity) || 0;
    const individualUnit = Number(it.additional_individual) || 0;
    const rateadoUnit = rateByPhysicalUnit * packageQty;
    const additionalUnit = individualUnit + rateadoUnit;
    const finalUnit = (Number(it.unit_price) || 0) + additionalUnit;
    return {
      ...it,
      additional_value: additionalUnit, // por unidade (individual + rateado)
      final_unit: finalUnit,
      total_value: finalUnit * qty,
    };
  });
};

// Campo de "pedido de produtos": escolhe um Grupo de Produtos e adiciona vários
// produtos (com quantidade e preço) numa tabela. Autossuficiente: carrega os
// dados do módulo de produtos. O estado persistido (group_id, price_table_id,
// items) sobe para o pai via onChange(patch).
// Também atende o tipo "product-table" (Tabela de Produtos sem valores): mesmo
// componente, ocultando preços/valores — só produto, quantidade e peso.
// Com `prefill_from` (itens copiados de um registro vinculado — ver
// libs/linkPrefill.js), a quantidade de cada item fica editável na própria linha.
// Com `show_prices`, o product-table exibe os preços copiados, só leitura.
// Props: field, onChange(patch), readOnly?
class ProductOrderField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      groups: [],
      groupData: null, // resultado de getGroupFull
      defaultTable: null,
      loadingGroup: false,
      // buffer da linha em construção
      selectedProduct: null,
      quantity: "",
      unitPrice: "",
      additionalValue: "",
      // valores dos campos adicionais (por índice de extra_fields)
      extraValues: {},
    };
  }

  componentDidMount() {
    products
      .listGroups(getTenant())
      .then((groups) => this.setState({ groups: groups || [] }))
      .catch(() => {});

    if (this.props.field.group_id) {
      this.loadGroup(this.props.field.group_id, { keepItems: true });
    }
  }

  get items() {
    return this.props.field.items || [];
  }

  // Variante sem a parte financeira (tipo "product-table"), salvo `show_prices`.
  get hidePrices() {
    return this.props.field.type === "product-table" && !this.props.field.show_prices;
  }

  // Preço só se digita no pedido; no product-table ele é, no máximo, copiado.
  get pricesEditable() {
    return this.props.field.type === "product-order";
  }

  get quantityEditable() {
    return !this.props.readOnly && !!this.props.field.prefill_from;
  }

  // Derivados financeiros. No product-table com preços copiados, os valores
  // vêm prontos do registro de origem: recalcular com o adicional (que não
  // existe aqui) apagaria o rateio original.
  derive = (items) =>
    this.props.field.type === "product-table" && this.props.field.show_prices
      ? items
      : recompute(items, this.props.field.additional_total);

  // Campos adicionais por item configurados no construtor (ex.: Lote).
  get extraFields() {
    return this.props.field.extra_fields || [];
  }

  // Código (SKU) do item. Preenchimentos salvos antes de o código ser gravado
  // no item não têm `product_code`; para esses, resolve pelo catálogo do grupo,
  // que já está carregado.
  codeFor = (item) => {
    if (item.product_code) {
      return item.product_code;
    }
    const catalog = (this.state.groupData && this.state.groupData.products) || [];
    const product = catalog.find((p) => p.id === item.product_id);
    return (product && product.code) || "";
  };

  loadGroup = (groupId, { keepItems } = {}) => {
    this.setState({ loadingGroup: true });
    products
      .getGroupFull(groupId)
      .then((groupData) => {
        const defaultTable = resolveDefaultTable(groupData.price_tables);
        this.setState({ groupData, defaultTable, loadingGroup: false });
        if (!keepItems) {
          this.props.onChange({
            group_id: groupId,
            price_table_id: defaultTable ? defaultTable.id : null,
            items: [],
          });
        }
      })
      .catch(() => this.setState({ loadingGroup: false }));
  };

  handleGroupChange = (event) => {
    const groupId = event.target.value;
    this.setState({ selectedProduct: null, quantity: "", unitPrice: "", additionalValue: "" });
    this.loadGroup(groupId, { keepItems: false });
  };

  handleSelectProduct = (event, product) => {
    if (!this.pricesEditable) {
      this.setState({ selectedProduct: product || null, unitPrice: "" });
      return;
    }
    const tableId = this.state.defaultTable ? this.state.defaultTable.id : null;
    const price = priceForTable(product, tableId);
    this.setState({
      selectedProduct: product || null,
      unitPrice: price == null ? "" : String(price),
    });
  };

  addItem = () => {
    const product = this.state.selectedProduct;
    const quantity = parseDecimal(this.state.quantity);
    const unitPrice = parseDecimal(this.state.unitPrice) || 0;
    if (!product || quantity === null || quantity <= 0) return;

    const packageQty = parseDecimal(product.package_quantity) || 0;
    const individual = parseDecimal(this.state.additionalValue) || 0;
    const raw = {
      product_id: product.id,
      product_name: product.name,
      product_code: product.code || "",
      unit_of_measure: product.unit_of_measure,
      package_quantity: product.package_quantity,
      currency: product.currency,
      quantity,
      unit_price: unitPrice,
      // Valor adicional individual do produto (por unidade).
      additional_individual: individual,
      // Total físico = quantidade x qtd por embalagem (ex.: 1 bolsa x 40 kg = 40 kg).
      total_quantity: quantity * packageQty,
      // Campos adicionais configurados no construtor (alinhados por índice).
      extras: this.extraFields.map((_, i) => this.state.extraValues[i] || ""),
    };
    const items = this.derive([...this.items, raw]);
    this.props.onChange({ items });
    this.setState({
      selectedProduct: null,
      quantity: "",
      unitPrice: "",
      additionalValue: "",
      extraValues: {},
    });
  };

  removeItem = (index) => {
    const items = this.derive(this.items.filter((_, i) => i !== index));
    this.props.onChange({ items });
  };

  // Vazio fica vazio enquanto se digita (senão o campo "pula" para 0).
  changeQuantity = (index, value) => {
    const quantity = value === "" ? "" : parseDecimal(value);
    if (quantity !== "" && (quantity === null || quantity < 0)) return;
    const items = this.derive(
      this.items.map((item, i) => (i === index ? updateItemQuantity(item, quantity) : item))
    );
    this.props.onChange({ items });
  };

  handleAdditionalTotalChange = (value) => {
    this.props.onChange({
      additional_total: value,
      items: recompute(this.items, value),
    });
  };

  grandTotal = () =>
    this.items.reduce((acc, it) => acc + (Number(it.total_quantity) || 0), 0);

  grandTotalValue = () =>
    this.items.reduce((acc, it) => acc + (Number(it.total_value) || 0), 0);

  unit = () => (this.items[0] && this.items[0].unit_of_measure) || "";

  currency = () => (this.items[0] && this.items[0].currency) || "BRL";

  render() {
    const { classes, field, readOnly } = this.props;
    const { groups, groupData, selectedProduct } = this.state;
    const groupProducts = (groupData && groupData.products) || [];

    return (
      <div className={classes.container}>
        <Typography variant="h6" className={classes.title}>
          {field.component ? field.component.label : "Itens do Pedido"}
        </Typography>

        {!readOnly && (
          <div className={classes.topRow}>
            <FormControl className={classes.formControl}>
              <InputLabel id="product-order-group">Grupo de Produtos</InputLabel>
              <Select
                labelId="product-order-group"
                value={field.group_id || ""}
                onChange={this.handleGroupChange}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {this.pricesEditable && (
              <TextField
                className={classes.additionalInput}
                label="Valor adicional total"
                type="number"
                value={field.additional_total == null ? "" : field.additional_total}
                onChange={(e) => this.handleAdditionalTotalChange(e.target.value)}
              />
            )}
          </div>
        )}

        {!readOnly && field.group_id && (
          <div className={classes.addRow}>
            <Autocomplete
              className={classes.productInput}
              options={groupProducts}
              value={selectedProduct}
              getOptionLabel={(o) => (o && o.name) || ""}
              getOptionSelected={(o, v) => o.id === (v && v.id)}
              onChange={this.handleSelectProduct}
              renderInput={(params) => (
                <TextField {...params} label="Produto" />
              )}
            />
            <TextField
              className={classes.qtyInput}
              label="Quantidade"
              type="number"
              value={this.state.quantity}
              onChange={(e) => this.setState({ quantity: e.target.value })}
            />
            {this.extraFields.map((ef, i) => (
              <TextField
                key={i}
                className={classes.priceInput}
                label={ef.label}
                type={ef.type_input === "date" ? "date" : "text"}
                InputLabelProps={
                  ef.type_input === "date" ? { shrink: true } : undefined
                }
                value={this.state.extraValues[i] || ""}
                onChange={(e) =>
                  this.setState({
                    extraValues: {
                      ...this.state.extraValues,
                      [i]: e.target.value,
                    },
                  })
                }
              />
            ))}
            {this.pricesEditable && (
              <TextField
                className={classes.priceInput}
                label="Preço unit."
                type="number"
                value={this.state.unitPrice}
                onChange={(e) => this.setState({ unitPrice: e.target.value })}
              />
            )}
            {this.pricesEditable && (
              <TextField
                className={classes.priceInput}
                label="Valor adicional"
                type="number"
                value={this.state.additionalValue}
                onChange={(e) =>
                  this.setState({ additionalValue: e.target.value })
                }
              />
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={this.addItem}
              className={classes.addButton}
            >
              Adicionar produto
            </Button>
          </div>
        )}

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {/* O código só aparece na variante sem valores, onde a tabela
                    tem espaço — no pedido as colunas financeiras já a lotam. */}
                {this.hidePrices && <TableCell>Código</TableCell>}
                <TableCell>Produto</TableCell>
                {this.extraFields.map((ef, i) => (
                  <TableCell key={i}>{ef.label}</TableCell>
                ))}
                <TableCell align="right">Qtd. por embalagem</TableCell>
                {/* <TableCell>Unidade</TableCell> */}
                <TableCell align="right">Quantidade</TableCell>
                {!this.hidePrices && (
                  <TableCell align="right">Preço unit.</TableCell>
                )}
                {!this.hidePrices && (
                  <TableCell align="right">Valor adicional</TableCell>
                )}
                {!this.hidePrices && (
                  <TableCell align="right">Valor final</TableCell>
                )}
                <TableCell align="right">Total</TableCell>
                {!this.hidePrices && (
                  <TableCell align="right">Valor total</TableCell>
                )}
                {!readOnly && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.items.map((item, index) => (
                <TableRow key={`${item.product_id}-${index}`}>
                  {this.hidePrices && <TableCell>{this.codeFor(item)}</TableCell>}
                  <TableCell>{item.product_name}</TableCell>
                  {this.extraFields.map((ef, i) => (
                    <TableCell key={i}>
                      {formatExtra(item.extras && item.extras[i], ef)}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    {formatQuantity(item.package_quantity)}
                  </TableCell>
                  {/* <TableCell>{item.unit_of_measure}</TableCell> */}
                  <TableCell align="right">
                    {this.quantityEditable ? (
                      <TextField
                        className={classes.qtyCell}
                        type="number"
                        inputProps={{ min: 0, style: { textAlign: "right" } }}
                        value={item.quantity == null ? "" : item.quantity}
                        onChange={(e) => this.changeQuantity(index, e.target.value)}
                      />
                    ) : (
                      item.quantity
                    )}
                  </TableCell>
                  {!this.hidePrices && (
                    <TableCell align="right">
                      {formatCurrency(item.unit_price, item.currency)}
                    </TableCell>
                  )}
                  {!this.hidePrices && (
                    <TableCell align="right">
                      {item.additional_value
                        ? formatCurrency(item.additional_value, item.currency)
                        : "-"}
                    </TableCell>
                  )}
                  {!this.hidePrices && (
                    <TableCell align="right">
                      {formatCurrency(item.final_unit, item.currency)}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {formatQuantity(item.total_quantity)}
                    {item.unit_of_measure ? ` ${item.unit_of_measure}` : ""}
                  </TableCell>
                  {!this.hidePrices && (
                    <TableCell align="right">
                      {formatCurrency(item.total_value, item.currency)}
                    </TableCell>
                  )}
                  {!readOnly && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label="remover"
                        onClick={() => this.removeItem(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {this.items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={
                      (this.hidePrices ? 5 : 8) +
                      this.extraFields.length +
                      (readOnly ? 0 : 1)
                    }
                    align="center"
                  >
                    Nenhum produto adicionado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {this.items.length > 0 && (
          <div className={classes.totalRow}>
            <Typography variant="subtitle1">
              <b>
                Total: {formatQuantity(this.grandTotal())}
                {this.unit() ? ` ${this.unit()}` : ""}
              </b>
            </Typography>
            {!this.hidePrices && (
              <Typography variant="subtitle1">
                <b>Valor total: {formatCurrency(this.grandTotalValue(), this.currency())}</b>
              </Typography>
            )}
          </div>
        )}
      </div>
    );
  }
}

const styles = (theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
  },
  topRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(3),
    flexWrap: "wrap",
  },
  formControl: {
    minWidth: 280,
    margin: theme.spacing(1, 0),
  },
  addRow: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  productInput: {
    width: 320,
  },
  qtyInput: {
    width: 120,
  },
  qtyCell: {
    width: 90,
  },
  priceInput: {
    width: 140,
  },
  additionalInput: {
    width: 180,
  },
  addButton: {
    height: 40,
  },
  tableContainer: {
    marginTop: theme.spacing(1),
  },
  totalRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(3),
    marginTop: theme.spacing(1),
  },
});

export default withStyles(styles)(ProductOrderField);
